/**
 * `<RolodexSpine>` — CODEX's persistent left-hand index (`.rlx-spine`). Phase 4 of `codex-v1`;
 * see `projects/codex-v1/plan.md` §6 Phase 4.
 *
 * **The one list.** The index lists every file — the LANCERS first, then the codex's entries,
 * grouped by class — and a click or Enter opens it in the reader beside it via `onSelect`. With
 * nothing open the reader shows a brief, never a second copy of this list (`decisions/0205`
 * point 2, F2).
 *
 * **UI state.** Search text and the keyboard cursor are local to this component (`useState`).
 * The armed class filters are lifted to `RolodexApp`, because the brief's BY CLASS tallies set
 * them too; none of it rides the URL.
 *
 * **Selection vs. the keyboard cursor — two different rows, two different marks (never colour
 * alone).** The *selected* row is `selectedId` (the file open in the reader) — it inverts to ink with an accent left rule. The *keyboard cursor* is a
 * SEPARATE row, this component's own arrow-key position — a `#f2efe4` fill with a 1px ink inset
 * ring. The two can coincide, differ, or (cursor only, nothing selected) exist alone; CSS never
 * conflates them because a screen reader / colour-blind reading has to tell them apart on shape
 * alone, not hue.
 *
 * **Keyboard scheme**, wired on a `window` listener for as long as this component is mounted
 * (i.e. exactly while the CODEX page is open):
 *   - `/` focuses the search field (skipped while a text field already has focus, so typing an
 *     actual `/` character into an input types it rather than re-stealing its own focus).
 *   - `↑`/`↓` step {@link spineCursorStep} over the CURRENT filtered/grouped order — never past
 *     either end (clamped, not wrapped) — even while the search field has focus, since a
 *     type-ahead-then-arrow gesture is exactly what a search field is for.
 *   - `Enter` opens whatever the cursor sits on (calls `onSelect`), wherever focus is.
 *   - `Escape` blurs the active element, leaving the index. When the spine is a folded
 *     `<860px` overlay (`open`, below), Escape also closes it (`onClose`).
 *   - `1`–`6` toggle the class-filter chip at that position ({@link SPINE_FILTER_CHIPS}), gated
 *     off while a text field has focus so typing a digit into search types the digit.
 *
 * **`open`/`onClose` — Phase 8a's `<860px` fold.** `RolodexApp`/`CodexApp` own whether the
 * spine is CURRENTLY an open overlay (the `☰ INDEX` toggle lives in the reader's head, outside
 * this component's tree) — this component only reads `open` to paint the
 * `.rlx-spine--open` class (inert above `<860px`, where the container query keeps the spine in
 * its normal flex slot regardless) and calls `onClose` on Escape. **"`☰ INDEX` opens the spine
 * focused"** (plan.md §6 Phase 8) is handled here too: a `useEffect` on `open`'s false→true
 * transition moves focus onto the spine root itself (`tabIndex={-1}`) rather than into the
 * search field — landing focus on the panel, not pre-arming a specific control, is what lets
 * every existing key (↑↓, `/`, 1–6) work immediately without an extra Tab.
 */
import { useEffect, useRef, useState } from "react";
import {
  SPINE_FILTER_CHIPS,
  filterSpineEntries,
  spineCursorStep,
  spineFlatOrder,
  spineGroups,
  type IndexKind,
  type SpineItem,
} from "./codexView";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../../lib/Icon";
import "../../../lib/searchField.css";
import { KindGlyph } from "./KindGlyph";

/** One index row: a {@link SpineItem}, plus a lancer's FILED / NO FILE mark. */
/** `image`: a lancer's uploaded portrait, shown in place of the kind glyph. */
export type SpineRowData = SpineItem & { filed?: boolean; image?: string };

/** Whether a key event originated in a text-entry control — a shortcut shouldn't hijack typing. */
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}

export function RolodexSpine({
  rows,
  selectedId,
  onSelect,
  kinds,
  onKinds,
  open,
  onClose,
}: {
  /** Every file: the lancers, then the codex entries not filed under one. */
  rows: readonly SpineRowData[];
  /** The open file's row id, or `null` with nothing open — drives the SELECTED row. */
  selectedId: string | null;
  /** Open a file in the reader. */
  onSelect: (entryId: string) => void;
  /** The armed class chips; empty narrows nothing. */
  kinds: ReadonlySet<IndexKind>;
  onKinds: (next: ReadonlySet<IndexKind>) => void;
  /** Whether the `<860px` fold currently shows the spine as an open overlay — see the module
   *  doc's "`open`/`onClose`" paragraph. Inert (ignored by CSS) at `>=860px`. */
  open: boolean;
  /** Close the `<860px` overlay — called on Escape. Harmless to call unfolded. */
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [cursorId, setCursorId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const spineRef = useRef<HTMLDivElement>(null);

  // "`☰ INDEX` opens the spine focused" — move focus onto the panel itself the moment `open`
  // flips false -> true, so every key below works without an extra Tab first.
  const wasOpenRef = useRef(open);
  useEffect(() => {
    if (open && !wasOpenRef.current) spineRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  const filtered = filterSpineEntries(rows, { query, kinds });
  const groups = spineGroups(filtered);
  const flatOrder = spineFlatOrder(filtered);

  // Read through refs inside the one `window` listener so it never needs to re-subscribe on
  // every keystroke/filter change — the combat keyboard root (`useCombatKeyboard.ts`) is the
  // established pattern for this in the repo.
  const flatOrderRef = useRef(flatOrder);
  flatOrderRef.current = flatOrder;
  const cursorIdRef = useRef(cursorId);
  cursorIdRef.current = cursorId;
  const kindsRef = useRef(kinds);
  kindsRef.current = kinds;
  const toggleKind = (kind: IndexKind) => {
    const next = new Set(kindsRef.current);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    onKinds(next);
  };
  const toggleKindRef = useRef(toggleKind);
  toggleKindRef.current = toggleKind;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        (document.activeElement as HTMLElement | null)?.blur();
        onClose();
        return;
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setCursorId(spineCursorStep(flatOrderRef.current, cursorIdRef.current, e.key === "ArrowDown" ? 1 : -1));
        return;
      }
      if (e.key === "Enter") {
        if (cursorIdRef.current !== null) onSelect(cursorIdRef.current);
        return;
      }
      if (isTextEntry(e.target)) return; // everything below would steal a literal character
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      const slot = Number(e.key);
      const chip = Number.isInteger(slot) ? SPINE_FILTER_CHIPS[slot - 1] : undefined;
      if (chip !== undefined) toggleKindRef.current(chip.kind);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSelect, onClose]);

  return (
    <div className={`rlx-spine${open ? " rlx-spine--open" : ""}`} ref={spineRef} tabIndex={-1}>
      <div className="rlx-sp-head">
        <span className="rlx-sp-title">INDEX</span>
        <span className="rlx-sp-count">{rows.length} ON FILE</span>
      </div>
      <div className="search-field rlx-sp-search">
        <Icon icon={faMagnifyingGlass} className="search-field-icon" />
        <input
          ref={searchRef}
          type="text"
          placeholder="search files…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="rlx-sp-search-input"
          aria-label="Search the codex index"
        />
        <kbd className="search-field-key">/</kbd>
      </div>
      <div className="rlx-sp-filters">
        {SPINE_FILTER_CHIPS.map((chip) => (
          <button
            key={chip.kind}
            type="button"
            className={`rlx-sp-chip${kinds.has(chip.kind) ? " on" : ""}`}
            aria-pressed={kinds.has(chip.kind)}
            onClick={() => toggleKind(chip.kind)}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <div className="rlx-sp-rows">
        {groups.map((group) => (
          <div key={group.kind}>
            <div className={`rlx-sp-group${group.kind === "lancer" ? " rlx-sp-group--lancer" : ""}`}>
              <span>
                {group.heading} · {group.entries.length}
              </span>
              <span className="rlx-sp-group-rule" />
            </div>
            {group.entries.map((entry) => (
              <SpineRow
                key={entry.id}
                entry={entry}
                selected={entry.id === selectedId}
                cursor={entry.id === cursorId}
                onSelect={onSelect}
              />
            ))}
          </div>
        ))}
      </div>
      <div className="rlx-sp-foot">
        ↑↓ MOVE · ENTER OPEN · ESC LEAVE
        <br />/ SEARCH · 1–6 FILTER BY CLASS
      </div>
    </div>
  );
}

function SpineRow({
  entry,
  selected,
  cursor,
  onSelect,
}: {
  entry: SpineRowData;
  selected: boolean;
  cursor: boolean;
  onSelect: (entryId: string) => void;
}) {
  const classes = ["rlx-sp-row"];
  if (entry.kind === "lancer") classes.push("rlx-sp-row--lancer");
  if (selected) classes.push("on");
  if (cursor) classes.push("kb");
  return (
    <button type="button" className={classes.join(" ")} title={`Open file — ${entry.name}`} onClick={() => onSelect(entry.id)}>
      <span className={`rlx-sp-ico${entry.image ? " rlx-sp-ico--art" : ""}`}>
        {entry.image ? <img src={entry.image} alt="" draggable={false} /> : <KindGlyph kind={entry.kind} />}
      </span>
      <span className="rlx-sp-txt">
        <span className="rlx-sp-name">{entry.name}</span>
        {entry.role !== undefined && entry.role !== "" && <span className="rlx-sp-sub">{entry.role}</span>}
        {entry.meta !== undefined && <span className="rlx-sp-meta">{entry.meta}</span>}
      </span>
      {entry.filed !== undefined && <span className={`rlx-sp-tag${entry.filed ? "" : " rlx-sp-tag--none"}`}>{entry.filed ? "FILED" : "NO FILE"}</span>}
    </button>
  );
}
